import { memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Div, filterHover, Icon, Loader, sideMenuControls, Text, useTheme } from "react-better-html";

import { type Organization } from "../../types/organization";

import { useReduxAction, useReduxState } from "../../utils/hooks";

function getNameShort(name: string): string {
   const nameParts = name.split(" ");

   if (nameParts.length > 1) return `${nameParts.at(0)?.[0] ?? ""}${nameParts.at(-1)?.[0] ?? ""}`.toUpperCase();

   return name.slice(0, 2).toUpperCase();
}

function SideBar() {
   const theme = useTheme();
   const navigate = useNavigate();

   const organizationsList = useReduxState((state) => state.organizations.list);
   const selectedOrganizationId = useReduxState((state) => state.organizations.selectedOrganizationId);

   const setSelectedOrganizationId = useReduxAction((actions) => actions.organizations.setSelectedOrganizationId);

   const onClickOrganization = useCallback((organization: Organization) => {
      setSelectedOrganizationId({
         organizationId: organization.id,
         isChange: true,
      });
      sideMenuControls.close();
   }, []);
   const onClickAddOrganization = useCallback(() => {
      navigate("/organization/create");
   }, [navigate]);

   const organizationColors = useMemo(
      () => [theme.colors.primary, theme.colors.success, theme.colors.warn, theme.colors.info],
      [theme.colors],
   );

   return (
      <Div.column height="100%" padding={theme.styles.space}>
         <Div.column flex={1} width="100%" gap={theme.styles.space}>
            {organizationsList ? (
               organizationsList?.map((organization) => {
                  const isSelected = organization.id === selectedOrganizationId;

                  const color = organizationColors[organizationsList.indexOf(organization) % organizationColors.length];

                  return (
                     <Div.row
                        position="relative"
                        width="100%"
                        aspectRatio={1}
                        backgroundColor={color}
                        borderRadius={theme.styles.borderRadius}
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        userSelect="none"
                        value={organization}
                        onClickWithValue={onClickOrganization}
                        key={organization.id}
                     >
                        <Text fontSize={20} fontWeight={700} letterSpacing={2} color={theme.colors.base}>
                           {getNameShort(organization.name)}
                        </Text>

                        <Div
                           position="absolute"
                           width={`calc(100% + ${theme.styles.gap}px)`}
                           aspectRatio={1}
                           border={`solid 2px ${color}`}
                           borderRadius={theme.styles.borderRadius + theme.styles.gap / 2}
                           opacity={!isSelected ? 0 : undefined}
                           transform={`scale(${isSelected ? 1 : 0.9})`}
                           transition={theme.styles.transition}
                        />
                     </Div.row>
                  );
               })
            ) : (
               <Div.row width="100%" alignItems="center" justifyContent="center">
                  <Loader />
               </Div.row>
            )}
         </Div.column>

         <Div.column width="100%">
            <Div.row
               width="100%"
               aspectRatio={1}
               backgroundColor={theme.colors.backgroundContent}
               border={`dashed ${theme.styles.borderWidth}px ${theme.colors.backgroundThree}`}
               borderRadius={theme.styles.borderRadius}
               filterHover={filterHover().z05}
               alignItems="center"
               justifyContent="center"
               cursor="pointer"
               onClick={onClickAddOrganization}
            >
               <Icon name="plus" />
            </Div.row>
         </Div.column>
      </Div.column>
   );
}

export default memo(SideBar);
